import React, { useState } from "react";
import { FileImage } from "@/components/FileImage";
import { UploadButton, EditableText, EditableNumber, EditableTextarea } from "@/components/fields";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, ChevronLeft, ChevronRight, Check, Pencil, ArrowRight } from "lucide-react";
import { TabHeader } from "@/components/royal";

const ATTRS = ["Tactics", "Leadership", "Melee", "Stealth", "Perception", "Diplomacy", "Magic"];

function AttributeBar({ name, value, editable, onChange, testId }) {
  return (
    <div className="space-y-1" data-testid={testId}>
      <div className="flex justify-between items-center">
        <span className="ht-uppercase-label">{name}</span>
        {editable ? (
          <Input type="number" min={1} max={100} value={value ?? 0}
            onChange={(e) => onChange(Number(e.target.value))}
            className="w-16 h-7 rounded-sm bg-background text-right tabular-nums" data-testid={`${testId}-input`} />
        ) : (
          <span className="tabular-nums text-sm">{value ?? 0}</span>
        )}
      </div>
      <div className="h-1 bg-muted rounded-sm overflow-hidden">
        <div className="h-full bg-gold" style={{ width: `${Math.min(100, value ?? 0)}%` }} />
      </div>
    </div>
  );
}

function ItemsGrid({ items = [], editable, onAdd, onChange, onRemove, onUploadImage }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="ht-uppercase-label">Items</div>
        {editable && (
          <Button variant="outline" size="sm" className="rounded-sm" onClick={onAdd} data-testid="add-item">
            <Plus strokeWidth={1.5} className="w-4 h-4 mr-1" /> Item
          </Button>
        )}
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">No items.</p>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {items.map((it, i) => (
            <div key={it.id} className="ht-panel p-3 space-y-2" data-testid={`item-card-${i}`}>
              <FileImage fileId={it.image_file_id} className="w-full aspect-square object-cover rounded-sm border border-gold/30" />
              {editable ? (
                <>
                  <UploadButton label="Image" accept="image/*" size="sm" onUploaded={(r) => onUploadImage(it.id, r.file_id)} testId={`item-image-${i}`} />
                  <EditableText value={it.name} editable onChange={(v) => onChange(it.id, "name", v)} placeholder="Item name" testId={`item-name-${i}`} />
                  <EditableTextarea value={it.description} editable rows={2} onChange={(v) => onChange(it.id, "description", v)} placeholder="Description" testId={`item-desc-${i}`} />
                  <Button variant="ghost" size="sm" className="rounded-sm text-destructive w-full" onClick={() => onRemove(it.id)} data-testid={`item-remove-${i}`}>
                    <Trash2 strokeWidth={1.5} className="w-4 h-4 mr-1" /> Remove
                  </Button>
                </>
              ) : (
                <>
                  <div className="font-medium text-sm">{it.name || "—"}</div>
                  <p className="text-xs text-muted-foreground">{it.description}</p>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CharacterSheet({ data, editable, onAttr, onItems, portraitEditable, onName, onPortrait, nameSaveHandler }) {
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(data.name || "");

  const items = data.items || [];
  const addItem = () => onItems((arr) => arr.push({ id: crypto.randomUUID(), name: "", description: "", image_file_id: null }));
  const changeItem = (id, k, v) => onItems((arr) => { const it = arr.find((x) => x.id === id); if (it) it[k] = v; });
  const uploadItem = (id, fid) => onItems((arr) => { const it = arr.find((x) => x.id === id); if (it) it.image_file_id = fid; });
  const removeItem = (id) => onItems((arr) => { const idx = arr.findIndex((x) => x.id === id); if (idx >= 0) arr.splice(idx, 1); });

  return (
    <div>
      <div className="mb-6">
        {onName ? (
          editingName ? (
            <div className="flex gap-2 items-center max-w-md">
              <Input value={nameDraft} onChange={(e) => setNameDraft(e.target.value)} className="rounded-sm bg-background font-heading text-xl h-11" data-testid="character-name-input" />
              <Button size="icon" className="rounded-sm h-11 w-11" data-testid="character-name-save"
                onClick={async () => { await nameSaveHandler(nameDraft); setEditingName(false); }}>
                <Check strokeWidth={1.5} className="w-5 h-5" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <h2 className="ht-title text-3xl" data-testid="character-name">{data.name || "Unnamed"}</h2>
              <Button variant="ghost" size="icon" className="rounded-sm h-8 w-8" onClick={() => { setNameDraft(data.name || ""); setEditingName(true); }} data-testid="character-name-edit">
                <Pencil strokeWidth={1.5} className="w-4 h-4" />
              </Button>
            </div>
          )
        ) : (
          <h2 className="ht-title text-3xl" data-testid="character-name">{data.name || "Unnamed"}</h2>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr_1fr] gap-6">
        <div className="space-y-3 order-2 lg:order-1">
          <div className="ht-uppercase-label">Attributes</div>
          {ATTRS.map((a) => (
            <AttributeBar key={a} name={a} value={data.attributes?.[a]} editable={editable}
              onChange={(v) => onAttr((d) => { d.attributes = d.attributes || {}; d.attributes[a] = v; })}
              testId={`attr-${a.toLowerCase()}`} />
          ))}
        </div>

        <div className="order-1 lg:order-2 space-y-3">
          <FileImage fileId={data.portrait_file_id} className="w-full aspect-[3/4] object-cover rounded-sm border border-gold/30" testId="character-portrait" />
          {portraitEditable && (
            <UploadButton label="Upload Portrait" accept="image/*" className="w-full" onUploaded={(r) => onPortrait(r.file_id)} testId="upload-portrait" />
          )}
        </div>

        <div className="order-3">
          <ItemsGrid items={items} editable={editable} onAdd={addItem} onChange={changeItem} onRemove={removeItem} onUploadImage={uploadItem} />
        </div>
      </div>
    </div>
  );
}

function ViperSheet({ data, editable, onAttr, onName, onBio, onImage }) {
  return (
    <div className="space-y-5">
      <div>
        {editable ? (
          <EditableText label="Name" value={data.name} editable onChange={onName} placeholder="Name" testId="viper-name" />
        ) : (
          <h3 className="ht-title text-3xl" data-testid="viper-name">{data.name || "Unnamed"}</h3>
        )}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-6">
        <div className="space-y-3 order-2 lg:order-1">
          <div className="ht-uppercase-label">Attributes</div>
          {ATTRS.map((a) => (
            <AttributeBar key={a} name={a} value={data.attributes?.[a]} editable={editable}
              onChange={(v) => onAttr((d) => { d.attributes = d.attributes || {}; d.attributes[a] = v; })}
              testId={`viper-attr-${a.toLowerCase()}`} />
          ))}
        </div>
        <div className="order-1 lg:order-2 space-y-3">
          <FileImage fileId={data.image_file_id} className="w-full max-w-[240px] aspect-[3/4] object-cover rounded-sm border border-gold/30" testId="viper-portrait" />
          {editable && (
            <UploadButton label="Upload Image" accept="image/*" className="max-w-[240px]" onUploaded={(r) => onImage(r.file_id)} testId="viper-upload" />
          )}
          {editable ? (
            <EditableTextarea label="Bio" value={data.description} editable rows={4} onChange={onBio} placeholder="Bio" testId="viper-bio" />
          ) : (
            <p className="text-sm text-muted-foreground" data-testid="viper-bio">{data.description}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Characters({ player, updatePlayer, editable, saveCharacterBasics }) {
  const [sub, setSub] = useState("player");
  const [courtIdx, setCourtIdx] = useState(0);
  const [viperIdx, setViperIdx] = useState(0);

  const char = player.character || {};
  const court = player.court || [];
  const others = player.other_characters || [];
  const vipers = player.vigil_vipers || [];
  const isWrey = (player.kingdom_name || "").toLowerCase().includes("wrey");

  const subs = [
    { key: "player", label: "Player Character" },
    { key: "court", label: "Court" },
    ...(isWrey ? [{ key: "vipers", label: "Vigil Vipers" }] : []),
    { key: "other", label: "Other Characters" },
  ];

  // Player Character handlers
  const pcAttr = (fn) => updatePlayer((p) => fn(p.character));
  const pcItems = (fn) => updatePlayer((p) => { p.character.items = p.character.items || []; fn(p.character.items); });

  // Court handlers
  const cur = court[courtIdx];
  const courtAttr = (fn) => updatePlayer((p) => fn(p.court[courtIdx]));
  const courtItems = (fn) => updatePlayer((p) => { p.court[courtIdx].items = p.court[courtIdx].items || []; fn(p.court[courtIdx].items); });
  const addCourt = () => updatePlayer((p) => {
    p.court = p.court || [];
    p.court.push({ id: crypto.randomUUID(), name: "New Member", portrait_file_id: null, attributes: {}, items: [] });
  });
  const removeCourt = () => updatePlayer((p) => { p.court.splice(courtIdx, 1); setCourtIdx((i) => Math.max(0, i - 1)); });

  // Other characters
  const addOther = () => updatePlayer((p) => { p.other_characters = p.other_characters || []; p.other_characters.push({ id: crypto.randomUUID(), name: "", description: "", image_file_id: null }); });
  const changeOther = (id, k, v) => updatePlayer((p) => { const o = p.other_characters.find((x) => x.id === id); if (o) o[k] = v; });
  const uploadOther = (id, fid) => updatePlayer((p) => { const o = p.other_characters.find((x) => x.id === id); if (o) o.image_file_id = fid; });
  const removeOther = (id) => updatePlayer((p) => { p.other_characters = p.other_characters.filter((x) => x.id !== id); });

  // Vigil Vipers (House Wrey only)
  const DEFAULT_ATTRS = () => ({ Tactics: 0, Leadership: 0, Melee: 0, Stealth: 0, Perception: 0, Diplomacy: 0, Magic: 0 });
  const moveToVipers = (id) => updatePlayer((p) => {
    p.other_characters = p.other_characters || [];
    p.vigil_vipers = p.vigil_vipers || [];
    const idx = p.other_characters.findIndex((x) => x.id === id);
    if (idx < 0) return;
    const [o] = p.other_characters.splice(idx, 1);
    p.vigil_vipers.push({
      id: o.id, name: o.name || "", description: o.description || "",
      image_file_id: o.image_file_id || null,
      attributes: o.attributes || DEFAULT_ATTRS(),
    });
  });
  const viper = vipers[viperIdx];
  const viperMut = (fn) => updatePlayer((p) => { if (p.vigil_vipers && p.vigil_vipers[viperIdx]) fn(p.vigil_vipers[viperIdx]); });
  const removeViper = () => { updatePlayer((p) => { p.vigil_vipers.splice(viperIdx, 1); }); setViperIdx((i) => Math.max(0, i - 1)); };
  const moveViperBack = () => {
    updatePlayer((p) => {
      const [v] = p.vigil_vipers.splice(viperIdx, 1);
      p.other_characters = p.other_characters || [];
      p.other_characters.push({ id: v.id, name: v.name || "", description: v.description || "", image_file_id: v.image_file_id || null });
    });
    setViperIdx((i) => Math.max(0, i - 1));
  };

  return (
    <div className="space-y-6">
      <TabHeader label="The Roster" title="Characters" />
      <div className="flex gap-1 border-b border-gold/20" data-testid="character-subtabs">
        {subs.map((s) => (
          <button key={s.key} onClick={() => setSub(s.key)} data-testid={`subtab-${s.key}`}
            className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${sub === s.key ? "border-gold text-gold font-medium" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {s.label}
          </button>
        ))}
      </div>

      {sub === "player" && (
        <CharacterSheet
          data={char} editable={editable} portraitEditable
          onAttr={pcAttr} onItems={pcItems}
          onName={saveCharacterBasics}
          nameSaveHandler={(name) => saveCharacterBasics({ name })}
          onPortrait={(fid) => saveCharacterBasics({ portrait_file_id: fid })}
        />
      )}

      {sub === "court" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" className="rounded-sm" disabled={courtIdx === 0} onClick={() => setCourtIdx((i) => i - 1)} data-testid="court-prev">
                <ChevronLeft strokeWidth={1.5} className="w-4 h-4" />
              </Button>
              <span className="ht-uppercase-label" data-testid="court-position">
                {court.length ? `${courtIdx + 1} / ${court.length}` : "0 / 0"}
              </span>
              <Button variant="outline" size="icon" className="rounded-sm" disabled={courtIdx >= court.length - 1} onClick={() => setCourtIdx((i) => i + 1)} data-testid="court-next">
                <ChevronRight strokeWidth={1.5} className="w-4 h-4" />
              </Button>
            </div>
            {editable && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="rounded-sm" onClick={addCourt} data-testid="add-court">
                  <Plus strokeWidth={1.5} className="w-4 h-4 mr-1" /> Member
                </Button>
                {cur && (
                  <Button variant="ghost" size="sm" className="rounded-sm text-destructive" onClick={removeCourt} data-testid="remove-court">
                    <Trash2 strokeWidth={1.5} className="w-4 h-4 mr-1" /> Remove
                  </Button>
                )}
              </div>
            )}
          </div>

          {cur ? (
            <CharacterSheet
              key={cur.id} data={cur} editable={editable} portraitEditable={editable}
              onAttr={(fn) => courtAttr(fn)}
              onItems={courtItems}
              onName={undefined}
              onPortrait={(fid) => courtAttr((d) => { d.portrait_file_id = fid; })}
            />
          ) : (
            <div className="ht-panel p-12 text-center text-muted-foreground" data-testid="court-empty">No court members.</div>
          )}
          {editable && cur && (
            <div className="max-w-md">
              <EditableText label="Member Name" value={cur.name} editable onChange={(v) => courtAttr((d) => { d.name = v; })} testId="court-member-name" />
            </div>
          )}
        </div>
      )}

      {sub === "vipers" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" className="rounded-sm" disabled={viperIdx === 0} onClick={() => setViperIdx((i) => i - 1)} data-testid="viper-prev">
                <ChevronLeft strokeWidth={1.5} className="w-4 h-4" />
              </Button>
              <span className="ht-uppercase-label" data-testid="viper-position">
                {vipers.length ? `${viperIdx + 1} / ${vipers.length}` : "0 / 0"}
              </span>
              <Button variant="outline" size="icon" className="rounded-sm" disabled={viperIdx >= vipers.length - 1} onClick={() => setViperIdx((i) => i + 1)} data-testid="viper-next">
                <ChevronRight strokeWidth={1.5} className="w-4 h-4" />
              </Button>
            </div>
            {editable && viper && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="rounded-sm" onClick={moveViperBack} data-testid="viper-move-back">
                  <ArrowRight strokeWidth={1.5} className="w-4 h-4 mr-1 rotate-180" /> To Other Characters
                </Button>
                <Button variant="ghost" size="sm" className="rounded-sm text-destructive" onClick={removeViper} data-testid="remove-viper">
                  <Trash2 strokeWidth={1.5} className="w-4 h-4 mr-1" /> Remove
                </Button>
              </div>
            )}
          </div>

          {viper ? (
            <ViperSheet
              key={viper.id} data={viper} editable={editable}
              onAttr={viperMut}
              onName={(v) => viperMut((d) => { d.name = v; })}
              onBio={(v) => viperMut((d) => { d.description = v; })}
              onImage={(fid) => viperMut((d) => { d.image_file_id = fid; })}
            />
          ) : (
            <div className="ht-panel p-12 text-center text-muted-foreground" data-testid="viper-empty">
              No Vigil Vipers yet. Move a character here from the “Other Characters” tab.
            </div>
          )}
        </div>
      )}

      {sub === "other" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="ht-uppercase-label">NPC Codex</div>
            {editable && (
              <Button variant="outline" size="sm" className="rounded-sm" onClick={addOther} data-testid="add-other">
                <Plus strokeWidth={1.5} className="w-4 h-4 mr-1" /> Entry
              </Button>
            )}
          </div>
          {others.length === 0 ? (
            <div className="ht-panel p-12 text-center text-muted-foreground" data-testid="other-empty">No characters encountered yet.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="other-grid">
              {others.map((o, i) => (
                <div key={o.id} className="ht-panel ht-panel-hover p-4 space-y-3" data-testid={`other-card-${i}`}>
                  <FileImage fileId={o.image_file_id} className="w-full aspect-video object-cover rounded-sm border border-gold/30" />
                  {editable ? (
                    <>
                      <UploadButton label="Image" accept="image/*" size="sm" onUploaded={(r) => uploadOther(o.id, r.file_id)} testId={`other-image-${i}`} />
                      <EditableText value={o.name} editable onChange={(v) => changeOther(o.id, "name", v)} placeholder="Name" testId={`other-name-${i}`} />
                      <EditableTextarea value={o.description} editable rows={3} onChange={(v) => changeOther(o.id, "description", v)} placeholder="Description" testId={`other-desc-${i}`} />
                      <Button variant="ghost" size="sm" className="rounded-sm text-destructive w-full" onClick={() => removeOther(o.id)} data-testid={`other-remove-${i}`}>
                        <Trash2 strokeWidth={1.5} className="w-4 h-4 mr-1" /> Remove
                      </Button>
                      {isWrey && (
                        <Button variant="outline" size="sm" className="rounded-sm w-full" onClick={() => moveToVipers(o.id)} data-testid={`other-to-viper-${i}`}>
                          <ArrowRight strokeWidth={1.5} className="w-4 h-4 mr-1" /> Move to Vigil Vipers
                        </Button>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="font-heading text-lg">{o.name || "—"}</div>
                      <p className="text-sm text-muted-foreground">{o.description}</p>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
